import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { persologElementQuestionsEditor_AddQuestionToPersologElementMutation } from "@relay/persologElementQuestionsEditor_AddQuestionToPersologElementMutation.graphql";

import { persologElementQuestionsEditor_EditQuestionFromPersologElementMutation } from "@relay/persologElementQuestionsEditor_EditQuestionFromPersologElementMutation.graphql";
import {
	persologElementQuestionsEditor_QuestionsFragment$data,
	persologElementQuestionsEditor_QuestionsFragment$key,
} from "@relay/persologElementQuestionsEditor_QuestionsFragment.graphql";

import { persologElementQuestionsEditor_RemoveQuestionFromPersologElementMutation } from "@relay/persologElementQuestionsEditor_RemoveQuestionFromPersologElementMutation.graphql";
import {
	ADD_QUESTION_TO_PERSOLOG_ELEMENT_MUTATION,
	EDIT_QUESTION_FROM_PERSOLOG_ELEMENT_MUTATION,
	PERSOLOG_ELEMENT_FRAGMENT,
	REMOVE_QUESTION_FROM_PERSOLOG_ELEMENT_MUTATION,
} from "./persolog-element-questions-editor.graphql";
import { PersologElementQuestionsEditorProps } from "./persolog-element-questions-editor.types";
import { PagesEditor } from "../../../../features/pages-editor";
import { PersologElementQuestionFormState } from "../persolog-element-question-form/persolog-element-question-form.types";
import { PersologElementQuestionFormDialog } from "../persolog-element-question-form-dialog";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

export const PersologElementQuestionsEditor = ({
	persologElementFragmentRef,
	onCreateQuestion,
	onDeleteQuestion,
}: PersologElementQuestionsEditorProps) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const [isQuestionFormDialogVisible, setIsQuestionFormDialogVisible] = useState(false);
	const [selectedQuestion, setSelectedQuestion] = useState<
		persologElementQuestionsEditor_QuestionsFragment$data["questions"][number] | null
	>(null);

	const toast = useRef<Toast>(null);
	const persologElement = useFragment<persologElementQuestionsEditor_QuestionsFragment$key>(
		PERSOLOG_ELEMENT_FRAGMENT,
		persologElementFragmentRef,
	);
	const questions = persologElement ? [...persologElement.questions] : [];

	const [addQuestionToPersologElement] =
		useMutation<persologElementQuestionsEditor_AddQuestionToPersologElementMutation>(
			ADD_QUESTION_TO_PERSOLOG_ELEMENT_MUTATION,
		);

	const [editQuestionFromPersologElement] =
		useMutation<persologElementQuestionsEditor_EditQuestionFromPersologElementMutation>(
			EDIT_QUESTION_FROM_PERSOLOG_ELEMENT_MUTATION,
		);

	const [removeQuestionFromPersologElement] =
		useMutation<persologElementQuestionsEditor_RemoveQuestionFromPersologElementMutation>(
			REMOVE_QUESTION_FROM_PERSOLOG_ELEMENT_MUTATION,
		);

	const onCompleted = (summary: string, detail: string) => {
		toast.current?.show({
			severity: "success",
			life: 3000,
			summary: summary || t("toasts.success"),
			detail: detail || t("toasts.successText"),
		});
	};

	const onError = (summary: string, detail: string) => {
		toast.current?.show({
			severity: "error",
			life: 3000,
			summary: summary || t("toasts.fail"),
			detail: detail || t("toasts.failText"),
		});
	};

	const handleOnCreateQuestion = (questionValues: PersologElementQuestionFormState) => {
		addQuestionToPersologElement({
			variables: {
				input: {
					elementId: persologElement!.id,
					title: questionValues.title,
					questionClasses: questionValues.questionClasses,
				},
			},
			onCompleted: () => {
				onCompleted(t("toasts.success"), t("educationalOffer.questions.createSuccess"));
				onCreateQuestion?.();
			},
			onError: () => onError(t("toasts.fail"), t("educationalOffer.questions.createFail")),
			updater: setDraft,
		});
	};

	const handleOnEditQuestion = (
		questionValues: PersologElementQuestionFormState,
		questionId: string,
	) => {
		editQuestionFromPersologElement({
			variables: {
				input: {
					elementId: persologElement!.id,
					questionId: questionId,
					newTitle: questionValues.title,
					newQuestionClasses: questionValues.questionClasses,
				},
			},
			onCompleted: () =>
				onCompleted(t("toasts.success"), t("educationalOffer.questions.updateSuccess")),
			onError: () => onError(t("toasts.fail"), t("educationalOffer.questions.updateFail")),
			updater: setDraft,
		});
	};

	const handleDeleteQuestion = (questionId: string) => {
		removeQuestionFromPersologElement({
			variables: {
				input: {
					elementId: persologElement!.id,
					questionId: questionId,
				},
			},
			onCompleted: () => {
				onCompleted(t("toasts.success"), t("educationalOffer.questions.deleteSuccess"));
				onDeleteQuestion?.();
			},
			onError: () => onError(t("toasts.fail"), t("educationalOffer.questions.deleteFail")),
			updater: setDraft,
		});
	};

	const handleQuestionFormOnSubmit = (questionValues: PersologElementQuestionFormState) => {
		selectedQuestion
			? handleOnEditQuestion(questionValues, selectedQuestion?.id)
			: handleOnCreateQuestion(questionValues);
		setIsQuestionFormDialogVisible(false);
		setSelectedQuestion(null);
	};

	const handleOnShowCreateQuestion = () => {
		setSelectedQuestion(null);
		setIsQuestionFormDialogVisible(true);
	};

	const handleOnShowEditQuestion = (pageId: string) => {
		const question = questions.find((page) => page.id === pageId);
		if (!question) return;

		setSelectedQuestion(question);
		setIsQuestionFormDialogVisible(true);
	};

	const handleOnHideQuestionFormDialog = () => {
		setSelectedQuestion(null);
		setIsQuestionFormDialogVisible(false);
	};

	const handleRearrangeQuestions = () => {
		// TODO: missing backend
	};

	return (
		<>
			<Toast ref={toast} />
			<PersologElementQuestionFormDialog
				isVisible={isQuestionFormDialogVisible}
				isCreating={selectedQuestion === null}
				persologElementFragmentRef={persologElement}
				questionFragmentRef={selectedQuestion}
				onSubmit={handleQuestionFormOnSubmit}
				onHide={handleOnHideQuestionFormDialog}
			/>
			<PagesEditor
				title={t("educationalOffer.questions.questions")}
				createLabel={t("educationalOffer.questions.addQuestion")}
				pages={questions}
				onShowCreatePage={handleOnShowCreateQuestion}
				onShowEditPage={handleOnShowEditQuestion}
				onDeletePage={handleDeleteQuestion}
				onRearrangePages={handleRearrangeQuestions}
			/>
		</>
	);
};
